import React from 'react';
import { Typography } from 'antd';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';
import styles from './primitives.module.css';

const { Text, Link } = Typography;

/** Actual server receipt — never fabricate. */
export interface ServerReceiptRefV0 {
  readonly receiptId: string;
  readonly receiptKind: string;
  readonly issuedAt?: string | null;
  readonly actorOrAuthorityRef?: string | null;
  readonly mutationOrActionRef?: string | null;
  readonly lineageRef?: string | null;
  readonly href?: string | null;
}

export interface ReceiptLinkProps {
  readonly presentationState: PrimitivePresentationState;
  /** Required for ready state. Absent → empty/failed; never fabricate. */
  readonly serverReceipt?: ServerReceiptRefV0 | null;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  /**
   * true only when an actual server receipt is passed.
   * Defaults to Boolean(serverReceipt?.receiptId).
   */
  readonly provesMutationComplete?: boolean;
  readonly 'data-testid'?: string;
}

export function ReceiptLink(props: ReceiptLinkProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('ReceiptLink must keep grantsAuthority/grantsApproval=false');
  }

  const receipt = props.serverReceipt;
  const hasServerReceipt =
    typeof receipt?.receiptId === 'string' && receipt.receiptId.trim().length > 0;
  const provesMutationComplete = props.provesMutationComplete ?? hasServerReceipt;

  if (provesMutationComplete && !hasServerReceipt) {
    throw new Error(
      'ReceiptLink cannot claim provesMutationComplete without an actual server receipt'
    );
  }

  const effectiveState: PrimitivePresentationState =
    props.presentationState === 'ready' && !hasServerReceipt ? 'empty' : props.presentationState;

  return (
    <PrimitiveSurfaceFrame
      title="영수증"
      presentationState={effectiveState}
      emptyMessage="서버 영수증이 없습니다."
      failedMessage="서버 영수증을 확인할 수 없습니다."
      data-testid={props['data-testid'] ?? 'receipt-link'}
      aria-label={hasServerReceipt && receipt ? `영수증 ${receipt.receiptId}` : '영수증 없음'}
    >
      {hasServerReceipt && receipt ? (
        <div
          className={styles.primitiveStack}
          data-proves-mutation-complete={String(provesMutationComplete)}
          data-server-receipt="true"
        >
          <div className={styles.primitiveFieldRow}>
            <span className={styles.primitiveFieldLabel}>receiptId</span>
            {receipt.href ? (
              <Link href={receipt.href}>{receipt.receiptId}</Link>
            ) : (
              <Text>{receipt.receiptId}</Text>
            )}
          </div>
          <div className={styles.primitiveFieldRow}>
            <span className={styles.primitiveFieldLabel}>kind</span>
            <Text>{receipt.receiptKind}</Text>
          </div>
          <div className={styles.primitiveFieldRow}>
            <span className={styles.primitiveFieldLabel}>issuedAt</span>
            <Text>{receipt.issuedAt ?? '—'}</Text>
          </div>
          <div className={styles.primitiveFieldRow}>
            <span className={styles.primitiveFieldLabel}>actor/authority</span>
            <Text>{receipt.actorOrAuthorityRef ?? '—'}</Text>
          </div>
          <div className={styles.primitiveFieldRow}>
            <span className={styles.primitiveFieldLabel}>mutation/action</span>
            <Text>{receipt.mutationOrActionRef ?? '—'}</Text>
          </div>
          {receipt.lineageRef ? (
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>lineage</span>
              <Text>{receipt.lineageRef}</Text>
            </div>
          ) : null}
        </div>
      ) : (
        <Text type="secondary" data-server-receipt="false" data-proves-mutation-complete="false">
          클라이언트 성공 객체를 영수증으로 표시하지 않습니다.
        </Text>
      )}
    </PrimitiveSurfaceFrame>
  );
}
